// Implements: F-IDN-026
/**
 * "Invitar admin" for one organization of Plataforma > Organizaciones. A platform admin holds no
 * seat in an organization they created, so its first admin can only arrive through an invitation;
 * Invitaciones lists only the organizations of the session's seats, so the invitation is issued
 * from here: one address, the organization role `org_admin`, no team.
 *
 * Mount it keyed by the organization's id (checklist 14): closing it unmounts it, and the answer
 * of an invitation sent before closing is not shown in another organization's dialog. The write
 * is sent once (`retry: false` in the API client, checklist 13). The result is the one the
 * Invitaciones screen shows (`IssuedLinks`): "Enviamos la invitación por correo a …" when the server
 * e-mailed it, the link to copy by hand otherwise. A refusal keeps this dialog open with the
 * server's sentence. While the invitation is on its way the dialog cannot be closed (Cancelar, the
 * close button, Escape, the scrim), as in Invitaciones: the server may already have issued it, and
 * its link is shown only once.
 */

import { useState } from "react";
import type { ReactElement } from "react";

import { invitationLink } from "../../../shared/api/invitationLink";
import { useTranslation } from "../../../shared/i18n";
import type { OrganizationView } from "../../../types/organization";
import { InviteAdminDialog } from "../components/InviteAdminDialog";
import { IssuedLinks } from "../components/IssuedLinks";
import type { IssuedEntry } from "../components/IssuedLinks";
import { useIssueInvitations } from "../hooks/useInvitations";
import { copyToClipboard } from "./InvitationsContainer";
import { writeProblem } from "./PeopleContainer";

export interface InviteAdminContainerProps {
  readonly organization: OrganizationView;
  readonly onClose: () => void;
}

export function InviteAdminContainer({ organization, onClose }: InviteAdminContainerProps): ReactElement {
  const { t } = useTranslation();
  const issue = useIssueInvitations(organization.id);
  const [problem, setProblem] = useState<string | null>(null);
  const [issued, setIssued] = useState<IssuedEntry | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  function send(email: string): void {
    if (issue.isPending) return;
    setProblem(null);
    issue.mutate(
      { emails: [email], role: "org_admin", teamId: null, teamRole: null },
      {
        onSuccess: ([outcome]) => {
          if (outcome === undefined) return;
          if (outcome.issued === undefined) {
            setProblem(writeProblem(outcome.error, t("common.state.error")));
            return;
          }
          setIssued({ email: outcome.email, link: invitationLink(outcome.issued.token), emailed: outcome.issued.email_sent });
        },
      },
    );
  }

  if (issued !== null) {
    return (
      <IssuedLinks
        entries={[issued]}
        copied={copied}
        onCopy={(entry) => {
          void copyToClipboard(entry.link).then((ok) => {
            setCopied(ok ? entry.email : null);
          });
        }}
        onClose={onClose}
      />
    );
  }
  return (
    <InviteAdminDialog
      organizationName={organization.name}
      pending={issue.isPending}
      problem={problem}
      onSubmit={send}
      onCancel={() => {
        if (!issue.isPending) onClose();
      }}
    />
  );
}
